import React from 'react';
import type { HeroComponent as HeroComponentType } from '../schemas/validation';
import { ButtonLink } from './shared/ButtonLink';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getCroppedImageStyles, hasCropApplied } from './ComponentEditModal/shared/cropUtils';
import { getFontFamily } from '../utils/fontLoader';

interface HeroComponentProps {
  component: HeroComponentType;
}

export const HeroComponent: React.FC<HeroComponentProps> = ({ component }) => {
  const { data: rawData } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const brandFonts = useBrandFonts();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Normalize data to handle alternative formats (e.g., from Roary AI)
  // Roary may create hero components with primaryButtonText/primaryButtonLink instead of buttons array
  const normalizeData = (d: typeof rawData) => {
    const normalized = { ...d };

    // Default size if not provided
    if (!normalized.size) {
      normalized.size = 'medium';
    }

    // Default alignment if not provided
    if (!normalized.alignment) {
      normalized.alignment = 'center';
    }

    // Convert legacy button format to buttons array if needed
    if (!normalized.buttons || normalized.buttons.length === 0) {
      const legacyData = d as any;
      const buttons: Array<{
        text: string;
        url: string;
        style: 'primary' | 'secondary' | 'outline' | 'ghost';
      }> = [];

      if (legacyData.primaryButtonText && legacyData.primaryButtonLink) {
        buttons.push({
          text: legacyData.primaryButtonText,
          url: legacyData.primaryButtonLink,
          style: 'primary' as const,
        });
      }

      if (legacyData.secondaryButtonText && legacyData.secondaryButtonLink) {
        buttons.push({
          text: legacyData.secondaryButtonText,
          url: legacyData.secondaryButtonLink,
          style: 'secondary' as const,
        });
      }

      if (buttons.length > 0) {
        normalized.buttons = buttons;
      }
    }

    return normalized;
  };

  const data = normalizeData(rawData);

  const alignmentClasses = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const sizeClasses: Record<string, string> = {
    small: 'py-12 md:py-16',
    medium: 'py-16 md:py-24',
    large: 'py-20 md:py-32',
  };

  const titleSizeClasses: Record<string, string> = {
    small: 'text-3xl md:text-4xl',
    medium: 'text-4xl md:text-5xl',
    large: 'text-5xl md:text-6xl lg:text-7xl',
  };

  // Get button styles: component customColors > theme colors > fallback
  const getButtonStyles = (style: string) => {
    const buttonBg = data.customColors?.buttonBg || themeStyles.hero?.buttonBg || '#3B82F6';
    const buttonText = data.customColors?.buttonText || themeStyles.hero?.buttonText || '#ffffff';

    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBg,
          color: buttonText,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBg,
          border: `2px solid ${buttonBg}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBg,
          border: `2px solid ${buttonBg}`,
        };
      case 'ghost':
        return {
          backgroundColor: 'transparent',
          color: buttonBg,
          border: 'none',
        };
      default:
        return {
          backgroundColor: buttonBg,
          color: buttonText,
          border: 'none',
        };
    }
  };

  // Helper to convert position enum to CSS background-position
  const getBackgroundPosition = (position?: string): string => {
    if (!position) return 'center center';
    const positionMap: Record<string, string> = {
      center: 'center center',
      top: 'center top',
      bottom: 'center bottom',
      left: 'left center',
      right: 'right center',
      'top-left': 'left top',
      'top-right': 'right top',
      'bottom-left': 'left bottom',
      'bottom-right': 'right bottom',
    };
    return positionMap[position] || 'center center';
  };

  // A photo hides the theme color, which is meant for a solid background.
  const hasPhoto = Boolean(
    data.backgroundImage?.enabled &&
    (data.backgroundImage.url || data.backgroundImage.cloudfrontUrl)
  );
  const textColor = hasPhoto
    ? data.customColors?.text || '#ffffff'
    : data.customColors?.text || themeStyles.hero?.text || data.textColor || '#ffffff';

  // Helper to determine if a string is a gradient
  const isGradient = (bg: string) =>
    bg?.startsWith('linear-gradient') || bg?.startsWith('radial-gradient');

  // Get the appropriate background style
  // PRIORITY: customColors (user explicit) > theme/brand package > stored data defaults
  const getBackgroundStyle = () => {
    // If component has background image, don't apply any background color
    if (data.backgroundImage?.enabled) {
      return {};
    }

    // User's explicit customColors override takes top priority
    if (data.customColors?.background) {
      return { backgroundColor: data.customColors.background };
    }

    // Theme / brand-package background (may be a gradient)
    const themeBg = themeStyles.hero?.background;
    if (themeBg) {
      if (isGradient(themeBg)) {
        return { backgroundImage: themeBg };
      } else {
        return { backgroundColor: themeBg };
      }
    }

    // Fallback to stored component data
    if (data.backgroundColor) {
      return { backgroundColor: data.backgroundColor };
    }

    return {};
  };

  return (
    <section className={`relative ${sizeClasses[data.size]}`} style={getBackgroundStyle()}>
      {/* Background Image */}
      {data.backgroundImage?.enabled &&
        (data.backgroundImage.url || data.backgroundImage.cloudfrontUrl) &&
        (() => {
          const bgImageUrl = data.backgroundImage.cloudfrontUrl || data.backgroundImage.url || '';
          const bgCrop = (data.backgroundImage as any).crop;
          const hasEffects = hasCropApplied(bgCrop);
          const cropStyles = getCroppedImageStyles(bgCrop);
          const rawSize = data.backgroundImage.size || 'cover';
          const baseImgStyles: React.CSSProperties = {
            objectFit: rawSize === 'contain' ? 'contain' : 'cover',
            objectPosition: getBackgroundPosition(data.backgroundImage.position),
          };

          return (
            <div
              className={`absolute inset-0 overflow-hidden ${
                data.backgroundImage.parallax ? 'parallax-bg' : ''
              }`}
            >
              <img
                src={bgImageUrl}
                alt=""
                data-seo-image-role="decorative"
                className="absolute inset-0 h-full w-full"
                style={hasEffects ? { ...baseImgStyles, ...cropStyles } : baseImgStyles}
              />
            </div>
          );
        })()}

      {/* Background Image Overlay */}
      {data.backgroundImage?.enabled && data.backgroundImage.overlay?.enabled && (
        <div
          className="absolute inset-0"
          style={{
            backgroundColor: data.backgroundImage.overlay.color || '#000000',
            opacity: data.backgroundImage.overlay.opacity ?? 0.5,
          }}
        />
      )}
      {hasPhoto && !data.backgroundImage?.overlay?.enabled && (
        <div
          className="absolute inset-0"
          data-hero-scrim="true"
          style={{ backgroundColor: '#000000', opacity: 0.5 }}
        />
      )}

      {/* Theme overlay (only if using theme background and not a gradient) */}
      {!data.backgroundColor &&
        !data.backgroundImage?.enabled &&
        themeStyles.hero?.overlay &&
        !isGradient(themeStyles.hero?.background || '') && (
          <div className={`absolute inset-0 ${themeStyles.hero?.overlay}`}></div>
        )}

      {/* Background Pattern (only if using theme background) */}
      {!data.backgroundColor &&
        !data.backgroundImage?.enabled &&
        themeStyles.hero?.pattern !== false && (
          <div className="absolute inset-0 opacity-10">
            <div
              className="w-full h-full"
              style={{
                backgroundImage: `url("data:image/svg+xml,${encodeURIComponent(
                  '<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"><g fill="none" fill-rule="evenodd"><g fill="#ffffff" fill-opacity="0.05"><circle cx="30" cy="30" r="2"/></g></g></svg>'
                )}")`,
                backgroundRepeat: 'repeat',
              }}
            ></div>
          </div>
        )}

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div
          className={`max-w-4xl ${
            data.alignment === 'center' ? 'mx-auto' : data.alignment === 'right' ? 'ml-auto' : ''
          }`}
        >
          {/* Tagline */}
          {data.tagline && (
            <p
              className={`opacity-80 text-sm md:text-base font-medium uppercase tracking-wide mb-4 ${
                alignmentClasses[data.alignment]
              }`}
              style={{ color: textColor }}
              dangerouslySetInnerHTML={{
                __html: renderTextWithLineBreaks(data.tagline),
              }}
            />
          )}

          {/* Title */}
          <h1
            className={`${
              titleSizeClasses[data.size]
            } font-bold mb-6 leading-tight ${alignmentClasses[data.alignment]}`}
            style={{
              color: textColor,
              whiteSpace: 'normal',
              ...headingFontStyle,
            }}
            dangerouslySetInnerHTML={{
              __html: renderTextWithLineBreaks(data.title),
            }}
          />

          {/* Subtitle */}
          {data.subtitle && (
            <p
              className={`text-lg md:text-xl opacity-90 mb-8 leading-relaxed max-w-3xl ${
                alignmentClasses[data.alignment]
              } ${data.alignment === 'center' ? 'mx-auto' : data.alignment === 'right' ? 'ml-auto' : ''}`}
              style={{ color: textColor, ...bodyFontStyle }}
              dangerouslySetInnerHTML={{
                __html: renderTextWithLineBreaks(data.subtitle),
              }}
            />
          )}

          {/* Buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-4 ${
              data.alignment === 'center'
                ? 'justify-center'
                : data.alignment === 'right'
                  ? 'justify-end'
                  : 'justify-start'
            }`}
          >
            {(data.buttons || []).map((button, index) => (
              <ButtonLink
                key={index}
                url={button.url}
                target={(button as any).target}
                className="px-8 py-4 text-lg font-semibold rounded-lg transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl inline-block text-center"
                style={getButtonStyles(button.style)}
              >
                {button.text}
              </ButtonLink>
            ))}
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent"></div>
    </section>
  );
};
